import React, { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import GenericTable from '../GenericTable';
import { getStatusStyle, languageIdToName, ROLE_ROUTES } from '@utils/constants';
import { fetchRejectedSentencesForGAAdmin, fetchSentencesForGAAdmin } from '@/store/thunks/gaadminThunk';

const SentenceDetailTableGAAdmin = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { role } = useSelector((store) => store.auth);
  const { selectedAssignedFileId, fileStatus, validationFileStatus, targetLanguageId } = useSelector((store) => store.gaAdmin);
  const [activeTab, setActiveTab] = useState("all");
  const [showRejected, setShowRejected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allSentences, setAllSentences] = useState([]);
  const [allRejectedSentences, setAllRejectedSentences] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;
  const isSentencesPage = location.pathname.includes(`${destinationRoute}/folders/files/sentences`);

  const handleAllSentencesClick = async () => {
    if (activeTab === "all") return; // Do nothing if already on All Sentences tab
    setActiveTab("all");
    setShowRejected(false);
    setPageIndex(0);
    setHasNextPage(true);
    setLoading(true);
    try {
      const res = await dispatch(fetchSentencesForGAAdmin(selectedAssignedFileId, fileStatus, validationFileStatus, targetLanguageId));
      console.log("fetchSentencesForGAAdmin response:", res);
      const data = res.data || [];
      console.log(data);
      if (Array.isArray(data)) {
        setAllSentences(data);
        setHasNextPage(data.length > pageSize);
      } else {
        setAllSentences([]);
        setHasNextPage(false);
      }
    } catch (error) {
      console.error("Error fetching all sentences:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectedSentencesClick = async () => {
    if (activeTab === "rejected") return; // Do nothing if already on Rejected Sentences tab
    setActiveTab("rejected");
    setShowRejected(true);
    setPageIndex(0);
    setHasNextPage(true);
    setLoading(true);
    try {
      const res = await dispatch(fetchRejectedSentencesForGAAdmin(selectedAssignedFileId));
      console.log("fetchRejectedSentencesForGAAdmin response:", res);
      const data = res || [];
      if (Array.isArray(data)) {
        setAllRejectedSentences(data);
        setHasNextPage(data.length > pageSize);
      } else {
        setAllRejectedSentences([]);
        setHasNextPage(false);
      }
    } catch (error) {
      console.error("Error fetching rejected sentences:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSentencesPage && selectedAssignedFileId && fileStatus && targetLanguageId) {
      setLoading(true);
      dispatch(fetchSentencesForGAAdmin(selectedAssignedFileId, fileStatus, validationFileStatus, targetLanguageId))
        .then((res) => {
          console.log("fetchSentencesForGAAdmin response:", res);
          const data = res.data || [];
          console.log(data);
          if (Array.isArray(data)) {
            setAllSentences(data);
            setHasNextPage(data.length > pageSize);
          } else {
            setAllSentences([]);
            setHasNextPage(false);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [dispatch, isSentencesPage, selectedAssignedFileId, fileStatus, targetLanguageId]);

  const paginatedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return allSentences.slice(start, end);
  }, [allSentences, pageIndex, pageSize]);

  const paginatedRejectedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return allRejectedSentences.slice(start, end);
  }, [allRejectedSentences, pageIndex, pageSize]);

  useEffect(() => {
    const activeData = showRejected ? allRejectedSentences : allSentences;
    setHasNextPage((pageIndex + 1) * pageSize < activeData.length);
  }, [allSentences, allRejectedSentences, showRejected, pageIndex, pageSize]);

  const sentenceColumns = [
    {
      id: 'serial_number',
      header: () => 'Sr. No.',
      cell: ({ row }) => (
        <div className="text-center text-sm font-['Semantic/Strong']">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: 'text-center',
    },
    {
      id: 'sentenceText',
      header: () => fileStatus === 'PENDING' ? 'CDAC Sentence Text' : 'CDAC Sentence Text',
      accessorKey: fileStatus === 'PENDING' ? 'cdacSentenceText' : 'cdacSentenceText',
      cell: ({ row }) => (
        <div className="max-w-4xl px-4 text-base text-gray-900 font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200 hover:bg-gray-50 py-2 rounded">
          {row.original[fileStatus === 'PENDING' ? 'cdacSentenceText' : 'cdacSentenceText'] || 'N/A'}
        </div>
      ),
    },
    {
      id: "gaSentenceText",
      header: () => "GA Sentence Text",
      accessorKey: "gaSentenceText",
      cell: ({ row }) => {
        const isUnchanged = row.original.gsSentenceText === "No change in CDAC source segment";
        const displayText = isUnchanged ? "No change" : row.original.gsSentenceText || "N/A";
        return (
          <div
            className={`max-w-4xl px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged
              ? "text-gray-500 bg-blue-50 hover:bg-blue-100"
              : "text-gray-900 hover:bg-gray-50"
              }`}
            title={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gsSentenceText || "No GA sentence text available"
            }
            aria-label={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gsSentenceText || "No GA sentence text available"
            }
          >
            {displayText}
          </div>
        );
      },
    },
    fileStatus === 'SENT_TO_VA' && (validationFileStatus === 'COMPLETED' || validationFileStatus === 'SUBMITTED' || validationFileStatus === 'SUBMITTED_TO_CDAC') && {
      id: 'translatedText',
      header: () => `GS Translated Text (${languageIdToName[targetLanguageId]})`,
      accessorKey: 'translatedTextGs',
      cell: ({ row }) => (
        <div className="max-w-4xl px-4 text-base text-gray-900 font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200 hover:bg-gray-50 py-2 rounded">
          {row.original.translatedTextGs || 'N/A'}
        </div>
      ),
    },
    fileStatus === 'SENT_TO_VA' && (validationFileStatus === 'COMPLETED' || validationFileStatus === 'SUBMITTED' || validationFileStatus === 'SUBMITTED_TO_CDAC') && {
      id: 'validatedText',
      header: () => `GS Validated Text (${languageIdToName[targetLanguageId]})`,
      accessorKey: 'validatedTextGs',
      cell: ({ row }) => (
        <div className="max-w-4xl px-4 text-base text-gray-900 font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200 hover:bg-gray-50 py-2 rounded">
          {row.original.validatedTextGs || 'N/A'}
        </div>
      ),
    },
    fileStatus === 'SENT_TO_VA' && (validationFileStatus === 'COMPLETED' || validationFileStatus === 'SUBMITTED' || validationFileStatus === 'SUBMITTED_TO_CDAC') && {
      id: 'translationStatus',
      header: () => 'Translation Status',
      accessorKey: 'translationStatus',
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.translationStatus);
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
        );
      },
      cellClassName: 'text-center',
    },
    fileStatus === 'SENT_TO_VA' && (validationFileStatus === 'COMPLETED' || validationFileStatus === 'SUBMITTED' || validationFileStatus === 'SUBMITTED_TO_CDAC') && {
      id: 'validationStatus',
      header: () => 'Validation Status',
      accessorKey: 'validationStatus',
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.validationStatus);
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
        );
      },
      cellClassName: 'text-center',
    },
  ].filter(Boolean);

  const rejectedSentenceColumns = [
    {
      id: 'serial_number',
      header: () => 'Sr. No.',
      cell: ({ row }) => (
        <div className="text-center text-sm font-['Semantic/Strong']">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: 'text-center',
    },
    {
      id: 'sentenceText',
      header: () => 'CDAC Sentence Text',
      accessorKey: 'cdacSentenceText',
      cell: ({ row }) => (
        <div className="max-w-4xl px-4 text-base text-gray-900 font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200 hover:bg-gray-50 py-2 rounded">
          {row.original.cdacSentenceText || 'N/A'}
        </div>
      ),
    },
    {
      id: "gaSentenceText",
      header: () => "GA Sentence Text",
      accessorKey: "gaSentenceText",
      cell: ({ row }) => {
        const isUnchanged = row.original.gaSentenceText === "No change in CDAC source segment";
        const displayText = isUnchanged ? "No change" : row.original.gaSentenceText || "N/A";
        return (
          <div
            className={`max-w-4xl px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged
              ? "text-gray-500 bg-blue-50 hover:bg-blue-100"
              : "text-gray-900 hover:bg-gray-50"
              }`}
            title={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
            aria-label={
              isUnchanged
                ? "No modifications made to the CDAC source segment"
                : row.original.gaSentenceText || "No GA sentence text available"
            }
          >
            {displayText}
          </div>
        );
      },
    },
    {
      id: 'rejectedBy',
      header: () => 'Rejected By',
      accessorKey: 'rejectedByTranslatorUserName',
      cell: ({ row }) => (
        <div className="text-center text-sm font-['Semantic/Strong']">
          {row.original.rejectedByTranslatorUserName || 'N/A'}
        </div>
      ),
      cellClassName: 'text-center',
    },
    {
      id: 'rejectionComment',
      header: () => 'Rejection Comment',
      cell: ({ row }) => (
        <div className="max-w-4xl px-4 text-base text-gray-900 font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none cursor-pointer transition-all duration-200 hover:bg-gray-50 py-2 rounded">
          {row.original.rejectionComment?.replace(/^"|"$/g, '') || 'No comment provided'}
        </div>
      ),
    },
    {
      id: 'createdAt',
      header: () => 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }) => {
        const uploadDate = row.original?.createdAt || row.createdAt;
        if (!uploadDate) return <div className="text-center text-sm font-['Semantic/Strong']">N/A</div>;
        try {
          const date = new Date(uploadDate);
          return (
            <div className="text-center text-sm font-['Semantic/Strong']">
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return <div className="text-center text-sm font-['Semantic/Strong']">{uploadDate}</div>;
        }
      },
      cellClassName: 'text-center',
    },
  ];

  return (
    <div className="flex flex-col flex-1">
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center mb-4">
              <div className="flex border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <button
                  className={`inline-flex items-center px-4 py-2 text-sm font-['Semantic/Strong'] ${activeTab === 'all' ? 'bg-[#ec7026] text-white' : 'bg-white text-[#ec7026] hover:bg-gray-50'
                    } transition-colors duration-200`}
                  onClick={handleAllSentencesClick}
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  All Sentences
                </button>
                <button
                  className={`inline-flex items-center px-4 py-2 text-sm font-['Semantic/Strong'] ${activeTab === 'rejected' ? 'bg-[#ec7026] text-white' : 'bg-white text-[#ec7026] hover:bg-gray-50'
                    } transition-colors duration-200`}
                  onClick={handleRejectedSentencesClick}
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Rejected Sentences
                </button>
              </div>
            </div>
            {loading ? (
              <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              </div>
            ) : !selectedAssignedFileId ? (
              <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                Please select a file to view sentences.
              </div>
            ) : showRejected ? (
              <div className="w-full overflow-x-auto">
                <GenericTable
                  data={paginatedRejectedSentences}
                  columns={rejectedSentenceColumns}
                  showPagination={true}
                  manualPagination={true}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  hasNextPage={hasNextPage}
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
                  emptyMessage="No rejected sentences found for this file"
                />
              </div>
            ) : allSentences === null || allSentences === undefined ? (
              <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                Loading sentences...
              </div>
            ) : !Array.isArray(allSentences) ? (
              <div className="p-6 text-center text-red-500 font-['Semantic/Strong']">
                Error: Unexpected sentence data format.
              </div>
            ) : allSentences.length === 0 ? (
              <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                <p>No sentences found.</p>
                <p className="mt-2 text-sm">
                  You have not been assigned any sentences. Please contact the administrator if this seems incorrect.
                </p>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <GenericTable
                  data={paginatedSentences}
                  columns={sentenceColumns}
                  showPagination={true}
                  manualPagination={true}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  hasNextPage={hasNextPage}
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
                  emptyMessage="No sentences available for this file"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SentenceDetailTableGAAdmin;